import { storeActions } from '~/utility/BinSentry-ui-utility';
import '../../common/bn-icon';
import '../../organizations/parts/organization-ancestors-bread-crumbs';
import '../../organizations/parts/farm-address';
import '../../organizations/props/organization-name';
import '../../work-orders/parts/work-order-grid';
import '../../notes/site-notes';
import { LitEntity, html, css, SirenActionMixin, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

class ScheduleItems extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.statusMap = new Map();
  }

  static get properties() {
    return {
      readyHref: { type: String, attribute: false },
      completedHref: { type: String, attribute: false },
      allComplete: { type: Boolean, attribute: false },
      readyWorkOrderCount: { type: Number, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
      }
      .header {
        margin: 0 .5rem .5rem;
      }
      .notes {
        margin: .5rem;
      }
      h3 {
        margin: 1rem .5rem .5rem;
      }
      .no-data {
        margin: 2rem;
        text-align: center;
        font-size: 1.5rem;
        color: var(--gray-70);
      }
      .no-data bn-icon {
        font-size: 3em;
        color: var(--gray-60);
      }
      bn-icon.success {
        color: var(--green-20);
      }
      bn-icon.error {
        color: var(--red-20);
      }
      h1 {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
    `];
  }

  render() {
    if (this.loading) {
      return html``;
    }

    const farmHref = this.getHref('https://api.binsentry.com/rel/farm');

    return html`
      <lit-entity .href="${this.getHref('https://api.binsentry.com/rel/work-orders')}" @update="${this._onUpdateWorkOrders}"></lit-entity>

      <div class="header">
        <h1>
          <a>
            <button @click="${this._onScheduleClick}" class="button button--outlined">
              <bn-icon name="far fa-less-than"></bn-icon>
            </button>
          </a>
          Scheduled Work Orders
        </h1>

        <h2>
          <organization-ancestors-bread-crumbs
            .href="${farmHref}"
            ancestorMinIndex="0"
            stop-at-type="binsentry"
          ></organization-ancestors-bread-crumbs>
        </h2>
        <farm-address .href="${farmHref}" include-map-links></farm-address>
      </div>

      <div class="notes">
        <site-notes href="${farmHref}" readonly></site-notes>
      </div>

      <div class="work-orders">
        ${renderIf(this.readyWorkOrderCount, () => html`<h3>Ready</h3>`)}
        <work-order-grid .href="${this.readyHref}" hide-address @update="${this._onUpdateReadyWorkOrders}">
          <div class="no-data" slot="no-data">${this._renderMessage()}</div>
        </work-order-grid>
        <h3>Completed</h3>
        <work-order-grid
          .href="${this.completedHref}"
          hide-address
          @complete="${this._onWorkOrderChange}"
          @problem="${this._onWorkOrderChange}"
        ></work-order-grid>
      </div>
    `;
  }

  _renderMessage() {
    if (!this.allComplete) {
      return html`
        <bn-icon name="fas fa-exclamation-circle" class="error"></bn-icon>
        <p>Not all Work Orders have completed successfully.</p>
      `;
    }
    return html`
      <bn-icon name="fas fa-check-circle" class="success"></bn-icon>
      <p>All Done!</p>
      <a href="/work-order-schedule">
        <button class="button button--contained">Return to Schedule</button>
      </a>
    `;
  }

  _onScheduleClick(e) {
    const notDone = this.readyWorkOrderCount > 0 || !this.allComplete;
    if (notDone && !confirm('Not all Work Orders have completed successfully.\n\nAre you sure you want to leave this location?')) {
      e.preventDefault();
    }
    storeActions.navigate('/work-order-schedule');
  }

  _onUpdateWorkOrders(e) {
    const { entity } = e.detail;
    const action = entity && entity.getAction('filter-work-orders');
    if (!action) {
      return;
    }
    const { url } = this.parseAction(action);
    url.searchParams.delete('type');
    url.searchParams.set('state', 'ready');
    this.readyHref = url.href;
    url.searchParams.set('state', 'completed');
    this.completedHref = url.href;
  }

  _onWorkOrderChange(e) {
    const { href, error } = e.detail;
    this.statusMap.set(href, error);
    this.allComplete = [...this.statusMap.values()].every(v => v === undefined);
  }

  _onUpdateReadyWorkOrders(e) {
    const { entity } = e.detail;
    this.readyWorkOrderCount = entity && entity.getSubEntitiesByRel('https://api.binsentry.com/rel/work-order').length;
  }
}

customElements.define('schedule-items', ScheduleItems);
